import type { CounterpartyType } from './counterparties';
import type { ModuleGrantCode, SystemGrantCode } from './grants';
import type { ServiceChatSide } from './service-requests';

// ── Бизнес-профили модуля «Орг.техника» (план `office-equipment-access-profiles-plan.md`, Р3) ──
//
// Реестр отвечает ровно на два вопроса: какими КОДАМИ наборов выдаётся профиль (форма учётки, Р7)
// и какая у профиля основная сторона обсуждения — та, которой его подписывают в админке и в
// документации. Всё остальное про профиль — состав прав, область, совместимые роли — живёт там,
// где и жило: в матрице, в каталоге наборов и в базе.
//
// ИМЯ НАМЕРЕННО НЕ `ACCESS_PROFILES`. Так уже называется перебор поставочных субъектов доступа в
// `permissions.ts` — «роль × тип контрагента × надстройка», то есть множество всех, кто в портале
// бывает; он нужен тестам матрицы и обратному поиску по праву. Здесь — четыре БИЗНЕС-роли одного
// модуля, и они не перечисляют субъектов: один человек штатно бывает и заявителем (по своей роли),
// и оператором (по набору). Два смысла у одного слова стоили бы ровно одного невнимательного
// чтения в ревью.
//
// РЕЕСТР НЕ УЧАСТВУЕТ В ВЫЧИСЛЕНИИ ПРАВ. `can` его не читает и читать не должен: состав набора
// живёт в базе (`grant_permissions`), и таблица, дублирующая состав, разъехалась бы с ним молча.
// Runtime-стороны обсуждения и адресаты писем из реестра тоже не выводятся — их считает
// `participantSidesOf` по правам, типу контрагента и назначению КОНКРЕТНОЙ заявки: назначенный
// внутренний исполнитель одновременно бывает `it` и `service`, а автор с набором «Ведение» —
// `customer` и `operator`. Реестр задаёт лишь основную сторону.
//
// Файл сознательно без единого рантайм-импорта (только `import type`): его константы читает
// `service-requests.ts`, и обратная зависимость по значению завела бы цикл модулей на ровном месте.

/**
 * Код набора, которым выдаётся «Ведение» модуля, и он же — код сегодняшней надстройки. Равенство
 * держит `SYSTEM_GRANT_CODES ... satisfies readonly RoleAddon[]`, а `satisfies SystemGrantCode`
 * здесь не даёт опечатке дожить до выката: строка, которой нет в каталоге кода, не скомпилируется.
 */
export const OFFICE_EQUIPMENT_OPERATOR_GRANT =
  'office_equipment_operator' satisfies SystemGrantCode;

/** Код набора ИТ-службы — по нему опознаётся сторона «Системный администратор» (Р9). */
export const OFFICE_EQUIPMENT_IT_GRANT = 'office_equipment_it_approver' satisfies SystemGrantCode;

/**
 * Код набора «работа исполнителем» — второй код профиля ИТ (Э6, миграция 0262).
 *
 * `satisfies ModuleGrantCode`, а не `SystemGrantCode`: набор МОДУЛЬНЫЙ. Разница не в оформлении —
 * системный код обязан быть кодом перенесённой надстройки и требует ключа в таблице сквозной
 * области, а у исполнителя нет ни того ни другого: сквозная область — ровно то, от чего его
 * отделяли. С этой строкой опечатка в коде набора перестала доживать до выката: строки, которой
 * нет в каталоге контрактов, компилятор не примет.
 */
export const OFFICE_EQUIPMENT_EXECUTOR_GRANT =
  'office_equipment_executor' satisfies ModuleGrantCode;

/**
 * Код набора «Заявитель» — четвёртый и последний код реестра, заведён этапом Э7 (миграция D).
 *
 * `satisfies ModuleGrantCode`, как у исполнителя, и по тем же двум причинам: надстройки
 * «заявитель» не было и не будет, а ключ в `GRANT_MODULE_WIDE_SCOPE` означал бы сквозную область —
 * то, чего у заявителя нет вовсе. С этой строкой опечатка в коде перестала доживать до выката, а
 * проверка «каждый код реестра существует в каталоге» стала ПОЛНОЙ: до Э7 она перечисляла
 * исключение, теперь исключений нет ни одного.
 *
 * Реестр называл код и до его заведения — иначе профиль «Заявитель» нельзя было бы описать ничем, а
 * он существует и без набора: для ролей с осью это базовое состояние роли (Р6).
 */
export const OFFICE_EQUIPMENT_REQUESTER_GRANT =
  'office_equipment_requester' satisfies ModuleGrantCode;

/**
 * Четыре бизнес-профиля модуля. Порядок — по ходу заявки: завёл → распределил → решил, чинить или
 * менять → починил.
 */
export const OFFICE_EQUIPMENT_PROFILES = ['requester', 'operator', 'it', 'service'] as const;
export type OfficeEquipmentProfileId = (typeof OFFICE_EQUIPMENT_PROFILES)[number];

/** Запись реестра: чем профиль выдаётся и как он называется. */
export interface OfficeEquipmentProfile {
  /**
   * Имя профиля для человека — то же слово, что у стороны обсуждения, но в именительном падеже:
   * `serviceChatSideLabels` подписывает АДРЕСАТА реплики («Системному администратору»), а здесь
   * подписывается сам профиль. Второго словаря из этого не выходит: подписи разного падежа и
   * разного назначения, и склеить их одной строкой значило бы испортить обе.
   */
  readonly label: string;
  /**
   * Коды наборов, которыми профиль выдаётся. Пустой список — не «забыли заполнить», а утверждение:
   * профиль выражается не набором (см. `service`).
   *
   * Строки, а не объединение кодов каталога: коды приезжают из ДВУХ разных перечней — системного
   * (`SystemGrantCode`) и модульного (`ModuleGrantCode`), — и сузить тип поля до одного из них
   * значило бы соврать про каталог, а до их объединения — завести третий тип ради поля, которое
   * заведомо принимает и пустой список (`service`). Проверку существования держат `satisfies` на
   * самих константах выше и тест каталога, сверяющий реестр с базой.
   *
   * Так было не всегда: до Э7 поле держало ещё и код, которого в каталоге не существовало вовсе
   * (`…_requester` заводила миграция D), — тогда сузить тип было нельзя вообще ничем.
   */
  readonly grants: readonly string[];
  /**
   * Тип контрагента, без которого профиля не бывает. Заполнен только у сервисного центра: его
   * права приходят от `COUNTERPARTY_TYPE_PERMISSIONS.service`, и учётка без контрагента прав
   * модуля не имеет вовсе. У прочих трёх — `null`: связь с контрагентом им не нужна и не мешает.
   */
  readonly counterpartyType: CounterpartyType | null;
  /**
   * Основная сторона обсуждения. Именно ОСНОВНАЯ, а не полный набор: стороны конкретной заявки
   * считает `participantSidesOf`, и у ИТ-службы при назначении их две (`it` и `service`).
   * `all` исключён типом — им можно адресовать, но им нельзя быть.
   */
  readonly primaryChatSide: Exclude<ServiceChatSide, 'all'>;
}

/**
 * Сам реестр. Три способа выдачи на четыре профиля — это свойство модели доступа (Р2), а не
 * недоделка: заявитель приходит базовой ролью (набор нужен ролям без модуля), оператор и ИТ —
 * наборами, сервисный центр — парой «роль `operator` + тип контрагента».
 */
export const OFFICE_EQUIPMENT_PROFILE_REGISTRY: Record<
  OfficeEquipmentProfileId,
  OfficeEquipmentProfile
> = {
  /**
   * Заявитель — базовое состояние роли (Р6): штаб, площадка и отдел заводят заявки и без всякого
   * набора. Код в списке всё равно назван: он заведён ради роли БЕЗ модуля — диспетчера, которому
   * иначе заявку не завести вовсе, — и с этим кодом его изменяющие действия сужаются до своих
   * заявок стражем авторства (`actsAsRequestCustomer`), потому что области у его роли нет. Менеджер
   * был здесь вторым до 10.09.2026: тот же круг он получил ролью (ADR 0181), и набор ему больше не
   * нужен — хотя выданный работает и у него, складываясь с ролью без всякого спора.
   */
  requester: {
    label: 'Заявитель',
    grants: [OFFICE_EQUIPMENT_REQUESTER_GRANT],
    counterpartyType: null,
    primaryChatSide: 'customer',
  },
  /** «Ведение» модуля: распределяет, ведёт статусы, принимает работу и закупает расходники. */
  operator: {
    label: 'Оператор',
    grants: [OFFICE_EQUIPMENT_OPERATOR_GRANT],
    counterpartyType: null,
    primaryChatSide: 'operator',
  },
  /**
   * Системный администратор — ЕДИНСТВЕННЫЙ профиль о двух кодах, и это не дробление ради дробления
   * (Р4, план аудита исполнителей). Координация («что с этой заявкой») и работа руками («я её и
   * чиню») — разные полномочия с разной областью: первое видит модуль сквозным, второе открывается
   * назначением на конкретную заявку. Выдаются они вместе и атомарно (Э8), но набором остаются
   * двумя — иначе назначенному исполнителю пришлось бы отдавать заодно сквозную видимость.
   */
  it: {
    label: 'Системный администратор',
    grants: [OFFICE_EQUIPMENT_IT_GRANT, OFFICE_EQUIPMENT_EXECUTOR_GRANT],
    counterpartyType: null,
    primaryChatSide: 'it',
  },
  /**
   * Сервисный центр НАБОРАМИ НЕ ВЫДАЁТСЯ ВОВСЕ, и пустой список здесь — сформулированный запрет
   * (Р11), а не пропуск. Профиль выражается парой «роль `operator` + тип контрагента `service`»:
   * набор scope-neutral по построению (ADR 0106, решение 2), а видимость подрядчика считает
   * предикат по контрагенту — «назначена ли заявка МОЕЙ компании». Набором такое не выражается:
   * он раздал бы права по всей базовой области, а её у подрядчика нет.
   */
  service: {
    label: 'Сервисный центр',
    grants: [],
    counterpartyType: 'service',
    primaryChatSide: 'service',
  },
};

/**
 * **Какие профили выданы этими кодами наборов** — подпись профиля в карточке учётки (Р7) и подсказка
 * формы «что у человека уже есть» (Э8).
 *
 * Спрашивается по КОДАМ, а не по правам (Р9), и аргумент здесь — те самые коды, которые учётка
 * приносит с собой: `AuthUser.grantCodes` у смотрящего и `UserDto.grantCodes` у всякой строки
 * списка. Сумма прав ответила бы иначе и хуже: собранный администратором набор с тем же составом
 * стал бы «Оператором», а половина профиля ИТ — полным профилем, потому что прав у неё почти
 * столько же.
 *
 * ПРОФИЛЬ БЕЗ КОДОВ НЕ СОВПАДАЕТ НИ С КЕМ, и это отдельная строка, а не следствие `every`: пустой
 * список кодов `every` проходит всегда, и «Сервисный центр» подписался бы КАЖДОЙ учётке компании —
 * включая ту, у которой наборов нет вовсе. Профиль этот выражен парой «роль `operator` + тип
 * контрагента `service`» (Р11), обе половины стоят в карточке своими полями, и выводить его отсюда
 * значило бы отвечать на вопрос, которого этой функции не задавали.
 *
 * ИТ ТРЕБУЕТ ОБОИХ КОДОВ (`every`, а не `some`). Половина профиля — не профиль: с одним
 * `…_executor` человека можно назначить исполнителем, но модуля он не видит, а с одним
 * `…_it_approver` — наоборот. Подписать такую учётку «Системным администратором» значило бы
 * спрятать ровно ту неполноту, ради которой Э8 и выдаёт пару одной транзакцией.
 *
 * Ответ — СПИСОК: профили независимы, и один человек штатно бывает и «Оператором», и «Заявителем».
 * Порядок — реестровый, то есть по ходу заявки: подпись не должна перескакивать от порядка кодов в
 * ответе сервера.
 */
export function officeEquipmentProfilesOf(
  grantCodes: readonly string[] | null | undefined,
): OfficeEquipmentProfileId[] {
  const codes = new Set(grantCodes ?? []);
  return OFFICE_EQUIPMENT_PROFILES.filter((id) => {
    const { grants } = OFFICE_EQUIPMENT_PROFILE_REGISTRY[id];
    return grants.length > 0 && grants.every((code) => codes.has(code));
  });
}
